import React, { useEffect, useState } from "react";
import { Modal, Dropdown } from "antd";
import {
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { languageOptions } from "../../utils";
import { ACTIONS_DROPDOWN_PROPS } from "../../constants/actionsDropdown";
import { InterpreterIcon } from "../../assets/svgs/icons";
import { getInterpreterImageUrl } from "../../pages/admin/Interpreter/helpers";

const MetaPill = ({ label, value }) => (
  <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs text-gray-700">
    <span className="shrink-0 text-gray-400">{label}</span>
    <span className="truncate font-medium text-gray-900">{value}</span>
  </span>
);

const InterpreterAvatar = ({ interpreter }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const avatarUrl = getInterpreterImageUrl(interpreter);
  const showImage = Boolean(avatarUrl) && !imgFailed;

  useEffect(() => {
    setImgFailed(false);
  }, [avatarUrl]);

  return (
    <div className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-300 bg-gradient-to-br from-gray-50 to-gray-500 text-white">
      {!showImage && <InterpreterIcon />}
      {showImage ? (
        <img
          src={avatarUrl}
          alt={interpreter?.user?.full_name}
          className="h-full w-full object-contain p-1.5 drop-shadow-sm"
          onError={() => setImgFailed(true)}
        />
      ) : null}
    </div>
  );
};

const VideoInfoModal = ({
  visible,
  onCancel,
  selectedVideo,
  onEdit,
  onDelete,
}) => {
  const languageLabel =
    languageOptions.find((opt) => opt.value === selectedVideo?.language)
      ?.label || "Not specified";

  const actionMenuItems = [
    { key: "edit", label: "Edit", icon: <EditOutlined /> },
    {
      key: "delete",
      label: "Delete",
      danger: true,
      icon: <DeleteOutlined />,
    },
  ];

  const handleActionMenuClick = ({ key }) => {
    if (key === "edit") {
      onEdit?.();
      return;
    }
    if (key === "delete") {
      onDelete?.();
    }
  };

  const actionsDropdown = (
    <Dropdown
      {...ACTIONS_DROPDOWN_PROPS}
      placement="bottomRight"
      menu={{
        items: actionMenuItems,
        onClick: handleActionMenuClick,
      }}
    >
      <button
        type="button"
        className="actions-dots-btn"
        onClick={(e) => e.stopPropagation()}
        aria-label="Open video actions"
      >
        <MoreOutlined />
      </button>
    </Dropdown>
  );

  return (
    <Modal
      title={<h3 className="m-0 pr-8">Video Info</h3>}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={1024}
      closeIcon={
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary">
          <CloseOutlined className="text-red-400" />
        </div>
      }
      modalRender={(node) => (
        <div className="relative isolate">
          {node}
          <div className="pointer-events-auto absolute right-[22px] top-[52px] z-[1002]">
            {actionsDropdown}
          </div>
        </div>
      )}
    >
      {selectedVideo && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[58fr_42fr]">
          <div className="relative flex aspect-video items-center justify-center overflow-hidden rounded-xl">
            <video
              src={selectedVideo.url}
              controls
              className="h-full w-full object-contain"
            />
          </div>

          <div className="flex min-h-0 flex-col">
            <div className="mb-4 flex w-fit items-center gap-3">
              <InterpreterAvatar interpreter={selectedVideo?.interpreter} />
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-gray-900">
                  {selectedVideo?.interpreter?.user?.full_name || "No presenter"}
                </div>
                <div className="truncate text-xs text-gray-500">
                  {selectedVideo?.interpreter?.user?.email || "—"}
                </div>
              </div>
            </div>

            <h3 className="mb-0.5 mt-0 line-clamp-2 text-base font-semibold capitalize text-gray-900">
              {selectedVideo.title}
            </h3>
            <p className="mb-0 mt-0 text-sm leading-relaxed text-gray-500">
              {selectedVideo.description?.trim() || "No description provided."}
            </p>

            <div className="mt-auto flex flex-wrap gap-2 border-t border-gray-300 pt-3">
              <MetaPill
                label="Associated to"
                value={selectedVideo?.organization?.name || "None"}
              />
              <MetaPill label="Language" value={languageLabel} />
              {selectedVideo?.interpreter?.user?.full_name && (
                <MetaPill
                  label="Presenter"
                  value={selectedVideo.interpreter.user.full_name}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default VideoInfoModal;
